import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const menuItems = [{
    id: 'inicio',
    label: 'Inicio',
    href: '#inicio'
  }, {
    id: 'services',
    label: 'Servicios',
    href: '#services'
  }, {
    id: 'bodegas',
    label: 'Bodegas',
    href: '#bodegas'
  }, {
    id: 'advantages',
    label: 'Ventajas',
    href: '#advantages'
  }, {
    id: 'contact',
    label: 'Contacto',
    href: '#contact'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      // Detect active section
      const sections = menuItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(menuItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (href: string) => {
    const id = href.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };
  return <>
      <motion.nav initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-black/80 backdrop-blur-xl border-b border-zinc-800/50 shadow-[0_0_30px_rgba(0,0,0,0.5)]' : 'bg-transparent'}`} data-id="element-133">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134">
          <div className="flex items-center justify-between h-16 md:h-20" data-id="element-135">
            {/* Logo */}
            <motion.a href="#inicio" onClick={e => {
            e.preventDefault();
            scrollToSection('#inicio');
          }} className="flex items-center" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-136">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rDDbVYSSWnpEBZ6FKUoh2o/LOGO_BODENOR.png" alt="Bodenor" className="h-8 md:h-10 w-auto drop-shadow-[0_0_10px_rgba(252,211,77,0.3)]" data-id="element-137" />
            </motion.a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1" data-id="element-138">
              {menuItems.map(item => <a key={item.id} href={item.href} onClick={e => {
              e.preventDefault();
              scrollToSection(item.href);
            }} className="relative px-4 py-2 text-sm font-semibold transition-colors duration-300 group" data-id="element-139">
                  <span className={`relative z-10 ${activeSection === item.id ? 'text-yellow-400' : 'text-gray-300 group-hover:text-white'}`} data-id="element-140">
                    {item.label}
                  </span>

                  {/* Active indicator */}
                  {activeSection === item.id && <motion.div layoutId="activeSection" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30
              }} data-id="element-141" />}

                  {/* Hover glow */}
                  <motion.div className="absolute inset-0 bg-yellow-400/0 group-hover:bg-yellow-400/10 rounded-lg transition-colors duration-300" whileHover={{
                scale: 1.05
              }} data-id="element-142" />
                </a>)}
            </div>

            {/* Mobile Menu Button */}
            <motion.button whileTap={{
            scale: 0.9
          }} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-gray-300 hover:text-yellow-400 transition-colors duration-300" aria-label="Toggle menu" data-id="element-143">
              {isMobileMenuOpen ? <XIcon size={24} data-id="element-144" /> : <MenuIcon size={24} data-id="element-145" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile menu indicator line */}
        {isScrolled && <motion.div initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} className="h-px bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent" data-id="element-146" />}
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-147">
        {isMobileMenuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-148" />

            {/* Menu Panel */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-gradient-to-br from-zinc-900 to-black border-l border-zinc-800 z-50 md:hidden overflow-hidden" data-id="element-149">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400/5 blur-3xl" data-id="element-150" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-400/5 blur-3xl" data-id="element-151" />

              {/* Grid background */}
              <div className="absolute inset-0 opacity-5" data-id="element-152">
                <div className="absolute inset-0" style={{
              backgroundImage: `
                    linear-gradient(rgba(252, 211, 77, 0.3) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(252, 211, 77, 0.3) 1px, transparent 1px)
                  `,
              backgroundSize: '40px 40px'
            }} data-id="element-153" />
              </div>

              <div className="relative z-10 flex flex-col h-full" data-id="element-154">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-zinc-800" data-id="element-155">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rDDbVYSSWnpEBZ6FKUoh2o/LOGO_BODENOR.png" alt="Bodenor" className="h-8 w-auto" data-id="element-156" />
                  <motion.button whileTap={{
                scale: 0.9
              }} onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-300 hover:text-yellow-400 transition-colors duration-300" data-id="element-157">
                    <XIcon size={24} data-id="element-158" />
                  </motion.button>
                </div>

                {/* Menu Items */}
                <nav className="flex-1 px-6 py-8" data-id="element-159">
                  <div className="space-y-2" data-id="element-160">
                    {menuItems.map((item, index) => <motion.a key={item.id} href={item.href} onClick={e => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }} initial={{
                  opacity: 0,
                  x: 50
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.1
                }} className={`block px-6 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${activeSection === item.id ? 'bg-yellow-400 text-black shadow-[0_0_20px_rgba(252,211,77,0.3)]' : 'text-gray-300 hover:bg-zinc-800 hover:text-white'}`} data-id="element-161">
                        {item.label}
                      </motion.a>)}
                  </div>
                </nav>

                {/* Footer */}
                <div className="p-6 border-t border-zinc-800" data-id="element-162">
                  <p className="text-sm text-gray-500 text-center" data-id="element-163">
                    Conectando Centroamérica
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}